import React from 'react';
import { Chip } from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import DrawIcon from '@mui/icons-material/Draw';
import NotesIcon from '@mui/icons-material/Notes';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import FlareIcon from '@mui/icons-material/Flare';
import SportsGolfIcon from '@mui/icons-material/SportsGolf';
import {
  brown,
  common,
  cyan,
  grey,
  lightBlue,
  lightGreen,
  orange,
  purple,
  red,
  yellow,
} from '@mui/material/colors';

interface SpellSchoolChipProps {
  label?: string | null;
  size?: 'small' | 'medium';
  onClick?: () => void;
  variant?: 'filled' | 'outlined';
  clickable?: boolean;
  color?: 'school' | 'default' | "primary" | "secondary" | "error" | "info" | "success" | "warning";
  school?: string | null;
}

interface ChipTone {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
}

const SCHOOL_TONES: Record<string, ChipTone> = {
  chronomancer: {
    backgroundColor: lightBlue[100],
    borderColor: lightBlue[300],
    textColor: grey[900],
  },
  elementalist: {
    backgroundColor: orange[100],
    borderColor: orange[300],
    textColor: grey[900],
  },
  enchanter: {
    backgroundColor: yellow[100],
    borderColor: yellow[300],
    textColor: grey[900],
  },
  illusionist: {
    backgroundColor: cyan[100],
    borderColor: cyan[300],
    textColor: grey[900],
  },
  necromancer: {
    backgroundColor: lightGreen[100],
    borderColor: lightGreen[300],
    textColor: grey[900],
  },
  sigilist: {
    backgroundColor: grey[300],
    borderColor: grey[400],
    textColor: grey[900],
  },
  soothsayer: {
    backgroundColor: purple[100],
    borderColor: purple[300],
    textColor: grey[900],
  },
  summoner: {
    backgroundColor: red[100],
    borderColor: red[300],
    textColor: grey[900],
  },
  thaumaturge: {
    backgroundColor: common.white,
    borderColor: grey[400],
    textColor: grey[900],
  },
  witch: {
    backgroundColor: brown[100],
    borderColor: brown[300],
    textColor: grey[900],
  },
};

const SCHOOL_ICONS: Record<string, React.ElementType> = {
  chronomancer: RestoreIcon,
  elementalist: WhatshotIcon,
  enchanter: AutoAwesomeIcon,
  illusionist: AutoFixHighIcon,
  necromancer: FaceRetouchingNaturalIcon,
  sigilist: DrawIcon,
  soothsayer: NotesIcon,
  summoner: BedtimeIcon,
  thaumaturge: FlareIcon,
  witch: SportsGolfIcon,
};

const getSchoolKeyFromLabel = (label: string): string | undefined => {
  const normalizedLabel = label.trim().toLowerCase();
  const words = normalizedLabel?.split(' ') ?? [];
  return words[words.length - 1];
}

export const getSpellSchoolTone = (
  school?: string | null
): ChipTone | undefined => {
  const schoolKey = getSchoolKeyFromLabel(school ?? '');

  if (!schoolKey) {
    return undefined;
  }

  return SCHOOL_TONES[schoolKey];
};

export const SpellSchoolChip: React.FC<SpellSchoolChipProps> = ({
  label,
  size = 'small',
  onClick,
  variant = 'filled',
  clickable = false,
  color = 'school',
  school = null
}) => {
  const schoolKey = school ?? getSchoolKeyFromLabel(label ?? '');

  if (!schoolKey) {
    return null;
  }

  const schoolTone = getSpellSchoolTone(schoolKey);
  const SchoolIcon = SCHOOL_ICONS[schoolKey.toLowerCase()];

  var bgColour = color.toString();
  if (bgColour === "school") {
    bgColour = schoolTone?.backgroundColor ?? "default";
  }


  return (
    <Chip
      clickable={clickable}
      label={label}
      size={size}
      variant={variant}
      icon={SchoolIcon ? <SchoolIcon /> : undefined}
      onClick={onClick}
      sx={{
        width: 'fit-content',
        alignSelf: 'flex-start',
        ...(onClick ? { cursor: 'pointer' } : {}),
        ...(schoolTone
          ? {
            bgcolor: bgColour,
            color: schoolTone.textColor,
            borderColor: schoolTone.borderColor,
            borderWidth: 1,
            borderStyle: 'solid',
          }
          : {}),
      }}
    />
  );
};
